import type { ColumnKind, Priority, Task } from "@/lib/types";

/**
 * How a board's columns order their cards. `manual` is the drag order (`position`),
 * the only one a drop within a column can change; every other sort is computed, so
 * under it a card can only be moved to another column.
 */
export type TaskSort = "manual" | "updated" | "due" | "created" | "priority";

export const TASK_SORTS: readonly { id: TaskSort; label: string; hint: string }[] = [
  { id: "manual", label: "Manual order", hint: "As arranged by dragging" },
  // A comment does not touch the card row, so this is edits and moves, not activity.
  { id: "updated", label: "Last edited", hint: "Most recently edited or moved first" },
  { id: "due", label: "Due date", hint: "Soonest due first, undated last" },
  { id: "created", label: "Created", hint: "Newest first" },
  { id: "priority", label: "Priority", hint: "Urgent first, then soonest due" },
];

export const isTaskSort = (value: unknown): value is TaskSort => TASK_SORTS.some((sort) => sort.id === value);

export const sortLabel = (sort: TaskSort): string => TASK_SORTS.find((entry) => entry.id === sort)!.label;

/** Core's order too (`services/tasks.ts`): urgent > high > medium > low. */
const PRIORITY_RANK: Record<Priority, number> = { urgent: 0, high: 1, medium: 2, low: 3 };

const ms = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const value = Date.parse(iso);
  return Number.isNaN(value) ? null : value;
};

/** Ascending, a missing value after every present one. */
const ascNullsLast = (a: number | null, b: number | null): number =>
  a === null ? (b === null ? 0 : 1) : b === null ? -1 : a - b;
/** Descending, a missing value after every present one. */
const descNullsLast = (a: number | null, b: number | null): number =>
  a === null ? (b === null ? 0 : 1) : b === null ? -1 : b - a;

export interface SortContext {
  /** The board's deadline: a card with no due date of its own is due then, as in core. */
  boardEndsAt: string | null;
  /** The column is done-kind: most recently completed first under any sort but manual. */
  columnKind?: ColumnKind | string;
}

/**
 * A comparator for one column's cards. Every sort ends on `position` and then the
 * id, so equal keys keep the hand-arranged order and the result never flickers.
 */
export function compareTasks(sort: TaskSort, context: SortContext): (a: Task, b: Task) => number {
  const byPosition = (a: Task, b: Task) => a.position - b.position || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  if (sort === "manual") return byPosition;
  const endMs = ms(context.boardEndsAt);
  const due = (task: Task) => ms(task.dueAt) ?? endMs;
  const priority = (a: Task, b: Task) => PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];

  if (context.columnKind === "done") {
    return (a, b) => descNullsLast(ms(a.completedAt), ms(b.completedAt)) || byPosition(a, b);
  }
  switch (sort) {
    case "updated":
      return (a, b) => descNullsLast(ms(a.updatedAt), ms(b.updatedAt)) || byPosition(a, b);
    case "created":
      return (a, b) => descNullsLast(ms(a.createdAt), ms(b.createdAt)) || byPosition(a, b);
    case "due":
      return (a, b) => ascNullsLast(due(a), due(b)) || priority(a, b) || byPosition(a, b);
    case "priority":
      return (a, b) => priority(a, b) || ascNullsLast(due(a), due(b)) || byPosition(a, b);
  }
}

/** A sorted copy of one column's cards. */
export const sortTasks = (tasks: readonly Task[], sort: TaskSort, context: SortContext): Task[] =>
  [...tasks].sort(compareTasks(sort, context));
